/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
import { useEffect, useMemo, useState } from 'react';
import { useIndexedDB } from 'react-indexed-db-hook';
import { getAllImagesFromArticle, transformActivities } from 'src/helpers';

import { useOfflineVideoLoader } from './useOfflineVideoLoader';

const flattenArray = (arr) =>
  arr
    ? arr.reduce(
        (acc, curr) =>
          acc.concat(Array.isArray(curr) ? flattenArray(curr) : curr),
        []
      )
    : null;

export const useDownloadingState = (module, totalSize) => {
  const [downloadedSize, setDownloadedSize] = useState(0);
  const [isActualInfo, setIsActualInfo] = useState(false);
  const { deleteVideo, downloadVideoByUrl, getRecord } =
    useOfflineVideoLoader();
  const { deleteRecord, getByID } = useIndexedDB('modules');
  const {
    add,
    deleteRecord: deleteImage,
    getAll: getAllImages,
  } = useIndexedDB('image');

  const [isDownloaded, setIsDownloaded] = useState(false);

  useEffect(() => {
    const init = async () => {
      const moduleDowloaded = await getByID(parseInt(module?.id, 10));
      setIsDownloaded(!!moduleDowloaded);
      setIsActualInfo(true);
    };
    if (module) {
      init();
    }
  }, [module]);

  useEffect(() => {
    if (downloadedSize === totalSize && totalSize !== 0) {
      setIsDownloaded(true);
    }
  }, [totalSize, downloadedSize]);

  const videosArr = useMemo(
    () =>
      flattenArray(module?.lessons.map((lesson) => lesson.activities))
        ?.filter(
          (activity) =>
            activity?.content_type === 'theory' && activity?.theory?.video_url
        )
        ?.map((activity) => ({
          activityId: activity?.id,
          url: activity.theory.video_url,
        })),
    [module?.lessons]
  );

  const imageArr = useMemo(
    () =>
      transformActivities(
        flattenArray(
          module?.lessons.map((lesson) =>
            lesson.activities
              ?.filter(
                (activity) =>
                  (activity?.content_type === 'article' &&
                    getAllImagesFromArticle(activity?.article?.html).length >
                      0) ||
                  (activity?.content_type === 'situation' &&
                    activity?.situation?.image)
              )
              ?.map((activity) => ({
                activityId: activity?.id,
                url: activity?.situation?.image
                  ? [activity?.situation?.image]
                  : getAllImagesFromArticle(activity?.article?.html),
              }))
          )
        )
      ),
    [module?.lessons]
  );
  const deleteImages = async (images) => {
    const imagesInDB = await getAllImages();

    const imageIDs = images?.reduce((acc, curr) => {
      const imageInDB = imagesInDB.find((el) => el.url === curr.url);
      if (imageInDB) {
        acc.push(imageInDB.id);
      }
      return acc;
    }, []);

    const uniqueImageIDs = [...new Set(imageIDs)];

    for (const imageID of uniqueImageIDs) {
      await deleteImage(imageID);
    }
  };

  const deleteModule = async () => {
    try {
      if (videosArr) {
        for (const video of videosArr) {
          await deleteVideo(video?.url);
        }
      }

      if (imageArr) {
        await deleteImages(imageArr);
      }

      await deleteRecord(module?.id);
      setIsDownloaded(false);
      setDownloadedSize(0);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('Delete module error: ', e);
    }
  };

  const getVideoSize = async (videoUrl) => {
    const response = await fetch(videoUrl);

    let totSize;
    const contentRangeHeader = response.headers.get('Content-Range');
    if (contentRangeHeader) {
      const [, rangeSize] = contentRangeHeader.match(/\/(\d+)/) || [];
      totSize = rangeSize ? parseInt(rangeSize, 10) : NaN;
    } else {
      totSize = parseInt(response.headers.get('Content-Length'), 10);
    }
    return totSize;
  };

  const saveImageInDB = async (image) => {
    const allImages = await getAllImages();
    const imageInDB = allImages.find((el) => el.url === image?.url);

    if (imageInDB) return;

    try {
      const response = await fetch(image?.url, {
        cache: 'no-cache',
        headers: {
          'Access-Control-Allow-Origin': '*',
        },
      });

      const blob = await response.blob();
      const sizeToAdd = await getVideoSize(image?.url);
      setDownloadedSize((prev) => prev + sizeToAdd);
      await add({ file: blob, url: image?.url });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('Save image in DB error: ', e);
    }
  };

  const startDownloading = async () => {
    if (imageArr) {
      for (const image of imageArr) {
        await saveImageInDB(image);
      }
    }

    if (videosArr) {
      for (const video of videosArr) {
        const record = await getRecord(video.url);
        await downloadVideoByUrl(record, setDownloadedSize);
      }
    }

    setIsDownloaded(true);
  };

  return {
    deleteModule,
    downloadedSize: Math.round((downloadedSize * 100) / (1024 * 1024)) / 100,
    isActualInfo,
    isDownloaded,
    startDownloading,
  };
};
